import { gql } from '@apollo/client';
import { FormControl, FormLabel, Text, useToast } from '@chakra-ui/react';
import { Permission } from '@tp-vision/roles-permissions';
import { isNil } from 'lodash';
import { useTranslation } from 'react-i18next';
import { useAuth } from '~auth/useAuth';
import { ConfigurationStatusType } from '~components/configurations/ConfigStateToggle';
import { DisplayControlsToggle } from '~components/displays/DisplayDetail/DisplayControlsToggle';
import { toAvailableControlToggleOptions } from '~components/displays/utils';
import { FormLabelPendingIndicator } from '~components/ui/FormLabelPendingIndicator';
import { ConfigOrientation } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { ToggleOptionsConfig } from '../ConfigControlsToggle';
import { checkStateToDisableTab } from '../ConfigDetailSettings';
import {
  ConfigDisplayOrientationToggle_ConfigFragment,
  useConfigUpdateOrientationMutation,
} from './__generated__/ConfigDisplayOrientationToggle.graphql';

interface Props {
  config: ConfigDisplayOrientationToggle_ConfigFragment;
  configStatus?: ConfigurationStatusType;
}

export function ConfigDisplayOrientationToggle({ config, configStatus }: Props) {
  const toast = useToast();
  const [updateOrientation, { loading }] = useConfigUpdateOrientationMutation();

  const { t } = useTranslation();
  const orientationToggleOptions: Array<ToggleOptionsConfig<ConfigOrientation>> = [
    { value: ConfigOrientation.Landscape, label: t('landscape') },
    { value: ConfigOrientation.Portrait, label: t('portrait') },
    ...(config.orientation?.supportedValues?.includes(ConfigOrientation.PortraitAnticlock)
      ? [{ value: ConfigOrientation.PortraitAnticlock, label: t('portraitAnticlock') }]
      : []),
  ];
  const handleChange = async (orientation: ConfigOrientation) => {
    try {
      await updateOrientation({
        variables: {
          input: {
            id: config.id,
            orientation,
          },
        },
        refetchQueries: ['CustomerConfigDetailPage'],
        awaitRefetchQueries: true,
      });
    } catch (error) {
      toast({
        status: 'error',
        title: t('cannotUpdateOrientation'),
        description: fromError(error, 'UpdateOrientation'),
      });
    }
  };

  const currentControlState = config.orientation?.reported;

  const controlOptions = toAvailableControlToggleOptions(
    orientationToggleOptions,
    config.orientation?.supportedValues,
  );

  const { verifyUserPermissions } = useAuth();

  const isConfigDisplaySettingsUpdateDisabled = !verifyUserPermissions([
    Permission.ConfigurationUpdate,
  ]);

  if (isNil(currentControlState)) {
    return null;
  }

  return (
    <FormControl>
      <FormLabel>
        {t('screenOrientation')}
        <FormLabelPendingIndicator isPending={loading} />
      </FormLabel>
      <Text color="gray.500" marginBottom="3">
        {t('screenOrientationText')}
      </Text>
      <DisplayControlsToggle
        name="orientationControlState"
        value={currentControlState}
        handleChange={handleChange}
        isLoading={loading}
        options={controlOptions}
        isDisabled={
          checkStateToDisableTab(configStatus) ? true : isConfigDisplaySettingsUpdateDisabled
        }
      />
    </FormControl>
  );
}

ConfigDisplayOrientationToggle.graphql = {
  fragments: {
    ConfigDisplayOrientationToggle_config: gql`
      fragment ConfigDisplayOrientationToggle_config on ConfigurationModel {
        id
        orientation {
          reported
          supportedValues
        }
      }
    `,
  },
  mutations: {
    ConfigUpdateOrientation: gql`
      mutation ConfigUpdateOrientation($input: ConfigurationUpdateOrientationInput!) {
        configurationUpdateOrientation(input: $input) {
          ...ConfigDisplayOrientationToggle_config
        }
      }
    `,
  },
};
